import React from "react";
import {
  getSelectionKind,
  getMaxHours,
  getMaxUnits,
  getPerUnitPrice,
  getHourlyRate,
  clampHours,
  clampQuantity,
  resolveServicePricing,
} from "../utils/servicePricing";

/** "unit" -> "units", "hour" -> "hours", "lash" -> "lashes" */
const pluralizeUnit = (label) => {
  const word = String(label || "unit").trim() || "unit";
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
};

/**
 * Customer-facing selector for services whose price depends on a choice:
 *   - per_unit / hourly -> quantity stepper (units / hours)
 *   - range (tiered)    -> the professional's named options
 * Renders nothing for fixed-price (and legacy/unsupported) services.
 *
 * Display only - the server re-resolves and re-validates every selection.
 */
export const ServicePricingSelector = ({
  service,
  selection,
  onChange,
  formatPrice,
  isMobile,
  accentColor = "#1d4ed8",
}) => {
  const kind = getSelectionKind(service);
  if (!kind) {
    return null;
  }

  const format = formatPrice || ((value) => `$${Number(value || 0).toFixed(2)}`);
  const line = resolveServicePricing(service, selection || {});
  if (!line) {
    return null;
  }

  const fieldStyle = {
    fontSize: isMobile ? "13px" : "14px",
    fontWeight: "600",
    color: "#334155",
  };

  if (kind === "tier") {
    const options = line.selectionOptions || [];
    const selectedId = selection?.tierId ? String(selection.tierId) : null;

    return (
      <div style={{ marginTop: "12px" }}>
        <div style={{ ...fieldStyle, marginBottom: "8px" }}>
          Choose an option
        </div>
        <div style={{ display: "grid", gap: "8px" }}>
          {options.map((option) => {
            const isSelected = selectedId === String(option.id);
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onChange({ tierId: option.id })}
                aria-pressed={isSelected}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  width: "100%",
                  padding: isMobile ? "10px 12px" : "12px 14px",
                  borderRadius: "8px",
                  border: isSelected
                    ? `2px solid ${accentColor}`
                    : "1px solid #cbd5e1",
                  background: isSelected ? "#eff6ff" : "#ffffff",
                  cursor: "pointer",
                  textAlign: "left",
                  font: "inherit",
                }}
              >
                <span
                  style={{
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: isSelected ? "700" : "600",
                    color: "#0f172a",
                  }}
                >
                  {option.label}
                  {option.durationMinutes ? (
                    <span
                      style={{
                        display: "block",
                        fontSize: "11px",
                        fontWeight: "400",
                        color: "#64748b",
                      }}
                    >
                      about {option.durationMinutes} min
                    </span>
                  ) : null}
                </span>
                <span
                  style={{
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "700",
                    color: accentColor,
                  }}
                >
                  {format(option.price)}
                </span>
              </button>
            );
          })}
        </div>
        {line.requiresSelection ? (
          <div style={{ marginTop: "6px", fontSize: "12px", color: "#b45309" }}>
            Pick an option to continue.
          </div>
        ) : null}
      </div>
    );
  }

  // quantity stepper (per_unit units / hourly hours)
  const isHourly = String(service?.pricingType || "").toLowerCase() === "hourly";
  const unitLabel = pluralizeUnit(
    isHourly ? "hour" : service?.unitLabel || service?.unit || "unit",
  );
  const unitPrice = isHourly ? getHourlyRate(service) : getPerUnitPrice(service);
  const max = isHourly ? getMaxHours(service) : getMaxUnits(service);
  const clamp = isHourly ? clampHours : clampQuantity;
  const quantity = line.quantity;

  const step = (delta) =>
    onChange({
      [isHourly ? "hours" : "quantity"]: Math.min(
        max,
        Math.max(1, clamp(service, quantity) + delta),
      ),
    });

  const buttonStyle = (disabled) => ({
    width: "34px",
    height: "32px",
    border: "none",
    background: "transparent",
    fontSize: "18px",
    fontWeight: "700",
    color: disabled ? "#cbd5e1" : accentColor,
    cursor: disabled ? "not-allowed" : "pointer",
  });

  return (
    <div
      style={{
        marginTop: "12px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
      }}
    >
      <span style={fieldStyle}>How many {unitLabel}?</span>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          border: "1px solid #cbd5e1",
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        }}
      >
        <button
          type="button"
          aria-label={`Decrease ${unitLabel}`}
          onClick={() => step(-1)}
          disabled={quantity <= 1}
          style={buttonStyle(quantity <= 1)}
        >
          −
        </button>
        <span
          style={{
            minWidth: "42px",
            textAlign: "center",
            fontWeight: "700",
            fontSize: "14px",
            color: "#0f172a",
          }}
        >
          {quantity}
        </span>
        <button
          type="button"
          aria-label={`Increase ${unitLabel}`}
          onClick={() => step(1)}
          disabled={quantity >= max}
          style={buttonStyle(quantity >= max)}
        >
          +
        </button>
      </div>
      <span style={{ fontSize: "12px", color: "#64748b" }}>
        {format(unitPrice)} each • up to {max}
      </span>
    </div>
  );
};

export const ActionNeededPanel = ({ items, isMobile, onItemClick }) => {
  if (!items?.length) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        marginTop: "10px",
        padding: "10px",
        borderRadius: "6px",
        border: "1px solid #fde68a",
        background: "#fffbeb",
      }}
    >
      <div
        style={{
          fontSize: "12px",
          color: "#92400e",
          fontWeight: "600",
          marginBottom: "6px",
        }}
      >
        Soft lock guidance: complete these before checkout.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => onItemClick(item)}
            style={{
              padding: isMobile ? "6px 10px" : "5px 8px",
              fontSize: isMobile ? "11px" : "12px",
              borderRadius: "999px",
              border: "1px solid #f59e0b",
              background: "#fff",
              color: "#92400e",
              cursor: "pointer",
              minHeight: "30px",
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export const BlockerChipsPanel = ({ blockers, onBlockerClick }) => {
  if (!blockers?.length) {
    return null;
  }

  return (
    <div
      style={{
        marginBottom: "12px",
        padding: "10px",
        borderRadius: "8px",
        background: "#fffbeb",
        border: "1px solid #fde68a",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {blockers.map((blocker) => (
          <button
            key={blocker.key}
            type="button"
            onClick={() => onBlockerClick(blocker)}
            style={{
              padding: "6px 10px",
              borderRadius: "999px",
              border: "1px solid #f59e0b",
              background: "#fff",
              color: "#92400e",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            {blocker.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export const NotesPanel = ({ warnings }) => {
  if (!warnings?.length) {
    return null;
  }

  return (
    <div
      style={{
        marginBottom: "12px",
        padding: "10px",
        borderRadius: "8px",
        background: "#eff6ff",
        border: "1px solid #bfdbfe",
      }}
    >
      <div
        style={{
          fontSize: "12px",
          fontWeight: "700",
          color: "#1d4ed8",
          marginBottom: "8px",
        }}
      >
        Notes
      </div>
      <div style={{ display: "grid", gap: "4px" }}>
        {warnings.map((warning) => (
          <div key={warning.key} style={{ fontSize: "12px", color: "#1e40af" }}>
            • {warning.label}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Optional free-text note the customer can leave for the professional
 * (a message, an accommodation request, or something to be aware of).
 * Shared by CustomerBooking and FreelancerBooking so both stay in sync.
 */
export const CustomerNoteField = ({
  value,
  onChange,
  maxLength = 500,
  isMobile,
}) => {
  const currentLength = String(value || "").length;
  const remaining = maxLength - currentLength;

  return (
    <div style={{ marginTop: "16px" }}>
      <label
        htmlFor="customer-booking-note"
        style={{
          display: "block",
          fontSize: isMobile ? "13px" : "14px",
          fontWeight: "600",
          color: "#334155",
          marginBottom: "6px",
        }}
      >
        Add a note for your professional{" "}
        <span style={{ fontWeight: "400", color: "#64748b" }}>(optional)</span>
      </label>
      <textarea
        id="customer-booking-note"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        maxLength={maxLength}
        rows={3}
        placeholder="Anything they should know? For example allergies, a request, or a message."
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "12px",
          border: "1px solid #d1d5db",
          borderRadius: "6px",
          fontSize: isMobile ? "12px" : "13px",
          fontFamily: "inherit",
          lineHeight: 1.5,
          color: "#0f172a",
          resize: "vertical",
          background: "#ffffff",
        }}
      />
      <div
        style={{
          marginTop: "4px",
          fontSize: "11px",
          textAlign: "right",
          color: remaining <= 50 ? "#b45309" : "#94a3b8",
        }}
      >
        {remaining} characters left
      </div>
    </div>
  );
};
